// AiInventoryImportModal — 在庫AIインポート v2 (2026-06).
//
// Chat の AIデータ取り込みと同じ UX:
//   1) Excel / CSV / 画像 / PDF をアップロード（テキスト貼り付けは折りたたみの副手段）
//   2) POST /mfg/inventory/ai-import/analyze → 検証済みプレビュー (OK/警告/エラー)
//   3) プレビューで編集・行削除・フィルター・エラーCSV出力
//   4) 「登録を確定」→ POST /commit（サーバー側で再検証・SKU発番・バッチ記録）
//   5) 結果サマリー + 一括取消（ロールバック）
import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Sparkle, UploadSimple, FileXls, DownloadSimple, CaretDown, CaretRight,
  Trash, ArrowCounterClockwise, WarningCircle, CheckCircle, XCircle, ArrowLeft,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { mfgApi } from './mfgApi';
import { useT } from '@/i18n/LanguageContext';
import { useAuth } from '@/auth/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/mfg/inventory/ai-import`;
const ACCEPT = '.xlsx,.csv,.png,.jpg,.jpeg,.webp,.pdf';

const STATUS_META = {
  ok:      { label: 'OK',   cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', Icon: CheckCircle },
  warning: { label: '警告', cls: 'bg-amber-50 text-amber-700 border-amber-200', Icon: WarningCircle },
  error:   { label: 'エラー', cls: 'bg-rose-50 text-rose-700 border-rose-200', Icon: XCircle },
};

const NONE = '__none__';

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.warning;
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-bold shrink-0 ${m.cls}`}>
      <m.Icon size={11} weight="fill" />{m.label}
    </span>
  );
}

/* 小型セル入力 */
function Cell({ value, onChange, w = 'w-28', testId }) {
  return (
    <Input value={value ?? ''} onChange={(e) => onChange(e.target.value)}
      className={`h-7 text-[11px] px-1.5 ${w}`} data-testid={testId} />
  );
}

function CellSelect({ value, onChange, options, w = 'w-28', placeholder = 'なし', testId, disabled }) {
  return (
    <Select value={value || NONE} onValueChange={(v) => onChange(v === NONE ? '' : v)} disabled={disabled}>
      <SelectTrigger className={`h-7 text-[11px] px-1.5 ${w}`} data-testid={testId}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="max-h-60">
        <SelectItem value={NONE}>{placeholder}</SelectItem>
        {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export default function AiInventoryImportModal({ open, onClose, defaultDepartmentId, onImported }) {
  const { t } = useT();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [settings, setSettings] = useState({ default_sku_rule_id: null, require_sku: false, rules: [] });
  const [step, setStep] = useState('input'); // input | review | result
  const [file, setFile] = useState(null);
  const [hint, setHint] = useState('');
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [data, setData] = useState(null);      // { rows, masters, summary, file_name }
  const [rows, setRows] = useState([]);
  const [mode, setMode] = useState('new_only');
  const [filter, setFilter] = useState('all');
  const [bulkRule, setBulkRule] = useState('');
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);
  const [rollingBack, setRollingBack] = useState(false);
  const [source, setSource] = useState('file');
  const fileRef = useRef(null);

  const reset = () => {
    setStep('input'); setFile(null); setHint(''); setPasteOpen(false); setPasteText('');
    setData(null); setRows([]); setMode('new_only'); setFilter('all'); setBulkRule('');
    setResult(null); setRollingBack(false); setSource('file');
  };

  const masters = data?.masters || { sku_rules: [], types: [], categories: [], suppliers: [], departments: [], uoms: [], makers: [], materials: [], locations: [] };

  useEffect(() => {
    if (!open) return;
    mfgApi.aiImportSettings().then((s) => setSettings({ ...s, rules: s.rules || [] })).catch(() => {});
  }, [open]);

  const saveSettings = async (body) => {
    try {
      const s = await mfgApi.saveAiImportSettings(body);
      setSettings({ ...s, rules: s.rules || settings.rules });
      toast.success(t('mfg.import.settingsSaved'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('mfg.error'));
    }
  };

  const downloadTemplate = async (sample) => {
    try {
      const res = await axios.get(`${API}/template`, { params: { sample: sample ? 1 : 0 }, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = sample ? '品目テンプレート_サンプル付き.xlsx' : '品目テンプレート.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch (_e) {
      toast.error('テンプレートのダウンロードに失敗しました');
    }
  };

  const analyze = async () => {
    setAnalyzing(true);
    try {
      let res;
      if (file) {
        const fd = new FormData();
        fd.append('file', file);
        if (hint) fd.append('hint', hint);
        res = (await axios.post(`${API}/analyze`, fd)).data;
        setSource('file');
      } else if (pasteText.trim()) {
        res = (await axios.post(`${API}/parse`, { text: pasteText, hint: hint || null })).data;
        setSource('paste');
      } else {
        toast.error('ファイルをアップロードするか、テキストを貼り付けてください');
        return;
      }
      if (!res.rows?.length) {
        toast.error('インポート可能な行がありません。');
        return;
      }
      setData(res);
      setRows(res.rows.map((r, i) => ({ ...r, _key: `r-${i}` })));
      (res.header_warnings || []).forEach((w) => toast.warning(w));
      toast.success(`${res.summary.total}件を解析しました（OK ${res.summary.ok} / 警告 ${res.summary.warning} / エラー ${res.summary.error}）`);
      setStep('review');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'ファイルを読み取れませんでした。形式を確認してください。');
    } finally {
      setAnalyzing(false);
    }
  };

  const setRow = (key, patch) => setRows((prev) => prev.map((r) => (r._key === key ? { ...r, ...patch } : r)));
  const removeRow = (key) => setRows((prev) => prev.filter((r) => r._key !== key));

  const applyBulkRule = (code) => {
    setBulkRule(code);
    setRows((prev) => prev.map((r) => (!(r.sku || '').trim() ? { ...r, sku_rule: code } : r)));
    toast.success('SKU未指定の行に品番ルールを適用しました');
  };

  const filtered = useMemo(() => (filter === 'all' ? rows : rows.filter((r) => r.status === filter)), [rows, filter]);
  const importable = useMemo(() => rows.filter((r) => r.status !== 'error' && r.action !== 'skip'), [rows]);
  const counts = useMemo(() => ({
    ok: rows.filter((r) => r.status === 'ok').length,
    warning: rows.filter((r) => r.status === 'warning').length,
    error: rows.filter((r) => r.status === 'error').length,
  }), [rows]);

  const downloadErrorCsv = (errRows) => {
    const source = errRows || rows.filter((r) => r.status === 'error').map((r) => ({ row_no: r.row_no, name: r.name, error: (r.messages || []).join(' / ') }));
    if (!source.length) { toast.info('エラー行はありません'); return; }
    const csv = ['行,品目名,エラー内容', ...source.map((r) => `${r.row_no},"${(r.name || '').replace(/"/g, '""')}","${(r.error || '').replace(/"/g, '""')}"`)].join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'import_errors.csv';
    a.click();
  };

  const commit = async () => {
    if (!importable.length) { toast.error('インポート可能な行がありません。'); return; }
    if (mode === 'allow_update' && !window.confirm('既存更新モードです。重複する既存品目が上書き更新されます。よろしいですか？')) return;
    if (!window.confirm(`${importable.length}件を登録します。よろしいですか？（エラー行は除外されます）`)) return;
    setCommitting(true);
    try {
      const res = await mfgApi.aiImportCommit({
        rows: rows.map(({ _key, ...r }) => r),
        mode,
        file_name: data?.file_name || null,
        source,
        hint: hint || null,
      });
      setResult(res);
      setStep('result');
      if (res.created > 0 || res.updated > 0) {
        toast.success(`登録 ${res.created}件 / 更新 ${res.updated}件`);
        onImported?.();
      }
      if (res.errors?.length) toast.error('一部の行のみ登録されました。エラー内容を確認してください。');
    } catch (e) {
      toast.error(e?.response?.data?.detail || '登録に失敗しました');
    } finally {
      setCommitting(false);
    }
  };

  const rollbackBatch = async () => {
    const batchId = result?.batch?.id;
    if (!batchId) return;
    if (!window.confirm(`AIインポートで登録した ${result.batch.item_count}件 を取消します。よろしいですか？（在庫や履歴のある品目はスキップされます）`)) return;
    setRollingBack(true);
    try {
      const res = await mfgApi.aiImportUndoBatch(batchId);
      setResult((prev) => ({ ...prev, batch: res.batch, rollback: res }));
      toast.success(`${res.summary.archived}件を取消しました${res.summary.skipped ? `（${res.summary.skipped}件はスキップ）` : ''}`);
      onImported?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'ロールバックに失敗しました');
    } finally {
      setRollingBack(false);
    }
  };

  const dedupe = (arr) => [...new Map(arr.map((o) => [o.value, o])).values()];
  const ruleOpts = dedupe(masters.sku_rules.map((r) => ({
    value: r.name && r.code ? `${r.name}（${r.code}）` : (r.name || r.code),
    label: r.name && r.code ? `${r.name}（${r.code}）` : (r.name || r.code),
  })));
  const typeOpts = dedupe(masters.types.map((t) => ({ value: t.label, label: t.label })));
  const catOpts = dedupe(masters.categories.map((c) => ({ value: c.name, label: c.name })));
  const uomOpts = dedupe(masters.uoms.map((u) => ({ value: u, label: u })));
  const makerOpts = dedupe(masters.makers.map((mk) => ({ value: mk, label: mk })));
  const supOpts = dedupe(masters.suppliers.map((s) => ({ value: s.name, label: s.name })));
  const matOpts = dedupe(masters.materials.map((mt) => ({ value: mt, label: mt })));
  const deptOpts = dedupe(masters.departments.map((d) => ({ value: d.name, label: d.name })));

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { reset(); onClose(); } }}>
      <DialogContent className="max-w-7xl max-h-[92vh] overflow-hidden flex flex-col" data-testid="ai-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="w-7 h-7 rounded-lg bg-teal-700 text-white flex items-center justify-center">
              <Sparkle size={15} weight="fill" />
            </span>
            在庫AIインポート
          </DialogTitle>
          <DialogDescription>
            Excel / CSV / 画像 / PDF ファイルをアップロードしてください。AIが品目情報を解析し、登録前にプレビューを表示します。
          </DialogDescription>
        </DialogHeader>

        {step === 'input' && (
          <div className="flex-1 overflow-y-auto space-y-4 pr-1" data-testid="ai-import-input">
            {/* Upload zone */}
            <button
              type="button"
              data-testid="ai-import-upload-zone"
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault(); setDragOver(false);
                const f = e.dataTransfer.files?.[0];
                if (f) setFile(f);
              }}
              className={`w-full rounded-xl border-2 border-dashed p-8 text-center transition-colors ${
                dragOver ? 'border-violet-400 bg-violet-50 dark:bg-violet-950/30' : 'border-zinc-300 dark:border-zinc-600 hover:border-violet-300 hover:bg-violet-50/40'
              }`}
            >
              <UploadSimple size={28} className="mx-auto text-violet-500" weight="bold" />
              <div className="mt-2 text-sm font-semibold text-zinc-800 dark:text-zinc-100">
                ファイルをアップロード（Excel / CSV / 画像 / PDF）
              </div>
              <div className="mt-1 text-[11px] text-zinc-500">.xlsx / .csv / .png / .jpg / .jpeg / .webp / .pdf · クリックまたはドラッグ&ドロップ</div>
              {file && (
                <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-violet-100 dark:bg-violet-900/40 text-violet-800 dark:text-violet-200 text-xs font-semibold" data-testid="ai-import-file-chip">
                  <FileXls size={14} weight="fill" />{file.name}
                  <span role="button" tabIndex={0} className="text-violet-500 hover:text-rose-500 cursor-pointer" data-testid="ai-import-file-clear"
                    onClick={(ev) => { ev.stopPropagation(); setFile(null); if (fileRef.current) fileRef.current.value = ''; }}
                    onKeyDown={() => {}}>✕</span>
                </div>
              )}
            </button>
            <input ref={fileRef} type="file" accept={ACCEPT} className="hidden" data-testid="ai-import-file-input"
              onChange={(e) => setFile(e.target.files?.[0] || null)} />

            {/* Hint */}
            <div>
              <Label className="text-xs font-semibold text-zinc-600 dark:text-zinc-300">追加指示（任意）</Label>
              <Input data-testid="ai-import-hint" value={hint} onChange={(e) => setHint(e.target.value)}
                placeholder="例: 全ての品目は購入部品として扱ってください" className="h-10 mt-1" />
            </div>

            {/* Template downloads */}
            <div className="rounded-lg border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800/50 p-3">
              <div className="text-xs font-bold text-zinc-600 dark:text-zinc-300 mb-2">テンプレート:</div>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" className="h-8 text-xs" data-testid="ai-import-template-blank"
                  onClick={() => downloadTemplate(false)}>
                  <DownloadSimple size={13} weight="bold" className="mr-1" />品目テンプレートをダウンロード
                </Button>
                <Button variant="outline" size="sm" className="h-8 text-xs" data-testid="ai-import-template-sample"
                  onClick={() => downloadTemplate(true)}>
                  <DownloadSimple size={13} weight="bold" className="mr-1" />サンプル付きテンプレートをダウンロード
                </Button>
              </div>
              <div className="mt-2 text-[11px] text-zinc-500">
                テンプレートは現在のマスターデータからドロップダウン付きで生成されます（品番ルール・タイプ・カテゴリ・単位・メーカー・仕入先・材質・ロケーション・生産/部門）。
              </div>
            </div>

            {/* A5 · 管理者設定: 既定の品番ルール / 品番の必須チェック（既定 OFF） */}
            <div className="rounded-lg border border-zinc-200 dark:border-zinc-700 p-3" data-testid="ai-import-settings-panel">
              <div className="text-xs font-bold text-zinc-600 dark:text-zinc-300 mb-2">{t('mfg.import.settingsTitle')}</div>
              <div className="flex flex-wrap items-end gap-3">
                <div className="min-w-[240px]">
                  <Label className="text-[11px] font-semibold text-zinc-600">{t('mfg.import.defaultSkuRule')}</Label>
                  <CellSelect
                    value={settings.default_sku_rule_id || ''}
                    onChange={(v) => saveSettings({ default_sku_rule_id: v || null, require_sku: settings.require_sku })}
                    options={(settings.rules || []).map((r) => ({ value: r.id, label: r.name && r.code ? `${r.name}（${r.code}）` : (r.name || r.code) }))}
                    w="w-60" placeholder={t('mfg.none')} disabled={!isAdmin}
                    testId="ai-import-default-rule"
                  />
                </div>
                <label className="flex items-center gap-2 text-xs text-zinc-700 dark:text-zinc-200">
                  <input type="checkbox" data-testid="ai-import-require-sku"
                    checked={!!settings.require_sku} disabled={!isAdmin}
                    onChange={(e) => saveSettings({ default_sku_rule_id: settings.default_sku_rule_id || null, require_sku: e.target.checked })}
                    className="h-4 w-4 rounded border-zinc-300 accent-teal-600" />
                  {t('mfg.import.requireSku')}
                </label>
              </div>
              <div className="mt-2 text-[11px] text-zinc-500">
                {t('mfg.import.defaultSkuRuleHint')} · {t('mfg.import.requireSkuHint')}
                {!isAdmin && <span className="ml-1 text-amber-700">（管理者のみ変更できます）</span>}
              </div>
            </div>

            {/* Secondary: paste */}
            <div className="rounded-lg border border-zinc-200 dark:border-zinc-700">
              <button type="button" data-testid="ai-import-paste-toggle"
                onClick={() => setPasteOpen((v) => !v)}
                className="w-full flex items-center gap-2 px-3 py-2.5 text-xs font-semibold text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800/60">
                {pasteOpen ? <CaretDown size={12} weight="bold" /> : <CaretRight size={12} weight="bold" />}
                テキスト貼り付けで取り込み
              </button>
              {pasteOpen && (
                <div className="px-3 pb-3">
                  <Textarea data-testid="ai-import-text" value={pasteText} onChange={(e) => setPasteText(e.target.value)}
                    placeholder={'例:\nM6 六角穴付ボルト SUS304 M6x20 100本 メーカー: TRUSCO 品番 T6-A-2010 単価150円\n0805 チップ抵抗 10kΩ 1% Panasonic ERJ-6ENF1002V 500個 12円'}
                    rows={7} className="font-mono text-xs" />
                  <div className="mt-1 text-[11px] text-zinc-500">{pasteText.length.toLocaleString()} / 20,000 文字 · ファイルが選択されている場合はファイルが優先されます</div>
                </div>
              )}
            </div>
          </div>
        )}

        {step === 'review' && (
          <div className="flex-1 overflow-hidden flex flex-col gap-3" data-testid="ai-import-review">
            {/* Summary + filters + mode */}
            <div className="flex flex-wrap items-center gap-2">
              {['all', 'ok', 'warning', 'error'].map((k) => (
                <button key={k} type="button" data-testid={`ai-import-filter-${k}`}
                  onClick={() => setFilter(k)}
                  className={`px-2.5 py-1 rounded-full border text-[11px] font-bold transition-colors ${
                    filter === k ? 'bg-violet-600 text-white border-violet-600' : 'bg-white dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 border-zinc-300 dark:border-zinc-600'
                  }`}>
                  {k === 'all' ? `すべて ${rows.length}` : `${STATUS_META[k].label} ${counts[k]}`}
                </button>
              ))}
              <div className="flex-1" />
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger className="h-8 w-44 text-xs" data-testid="ai-import-mode">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="new_only">新規登録のみ（重複はスキップ）</SelectItem>
                  <SelectItem value="allow_update">既存更新を許可</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" className="h-8 text-xs" data-testid="ai-import-error-csv"
                onClick={() => downloadErrorCsv()}>
                <DownloadSimple size={13} className="mr-1" />エラーCSV
              </Button>
            </div>
            {mode === 'allow_update' && (
              <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5" data-testid="ai-import-update-warning">
                ⚠ 既存更新モード: 「既存を更新」を選択した行は、既存品目の内容が上書き更新されます。
              </div>
            )}
            {rows.some((r) => !(r.sku || '').trim()) && (
              <div className="flex items-center gap-2 text-[11px]">
                <span className="font-semibold text-zinc-600 dark:text-zinc-300">品番ルール一括適用（SKU未指定行）:</span>
                <Select value={bulkRule || NONE} onValueChange={(v) => v !== NONE && applyBulkRule(v)}>
                  <SelectTrigger className="h-7 w-64 text-[11px]" data-testid="ai-import-bulk-rule">
                    <SelectValue placeholder="品番ルールを選択..." />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    <SelectItem value={NONE}>選択してください</SelectItem>
                    {ruleOpts.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Grid */}
            <div className="flex-1 overflow-auto border border-zinc-200 dark:border-zinc-700 rounded-lg">
              <table className="text-[11px] border-collapse min-w-max">
                <thead className="sticky top-0 bg-zinc-100 dark:bg-zinc-800 z-10">
                  <tr>
                    {['#', '状態', '品目名＊', '品番', '品番ルール', 'タイプ', 'カテゴリ', '単位', 'メーカー', '仕入先', '材質', '生産', '発注点', '単価', '処理', 'メッセージ', ''].map((h) => (
                      <th key={h} className="px-1.5 py-1.5 text-left font-bold text-zinc-600 dark:text-zinc-300 whitespace-nowrap border-b border-zinc-200 dark:border-zinc-700">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => (
                    <tr key={r._key} className="border-b border-zinc-200 dark:border-zinc-800 align-top" data-testid={`ai-import-row-${r.row_no}`}>
                      <td className="px-1.5 py-1 text-zinc-400 tabular-nums">{r.row_no}</td>
                      <td className="px-1.5 py-1"><StatusBadge status={r.status} /></td>
                      <td className="px-1.5 py-1"><Cell value={r.name} onChange={(v) => setRow(r._key, { name: v })} w="w-44" testId={`ai-row-name-${r.row_no}`} /></td>
                      <td className="px-1.5 py-1"><Cell value={r.sku} onChange={(v) => setRow(r._key, { sku: v })} w="w-28" /></td>
                      <td className="px-1.5 py-1"><CellSelect value={r.sku_rule} onChange={(v) => setRow(r._key, { sku_rule: v })} options={ruleOpts} w="w-36" /></td>
                      <td className="px-1.5 py-1"><CellSelect value={r.type} onChange={(v) => setRow(r._key, { type: v })} options={typeOpts} w="w-24" /></td>
                      <td className="px-1.5 py-1"><CellSelect value={r.category} onChange={(v) => setRow(r._key, { category: v })} options={catOpts} w="w-28" /></td>
                      <td className="px-1.5 py-1"><CellSelect value={r.uom} onChange={(v) => setRow(r._key, { uom: v })} options={uomOpts} w="w-16" /></td>
                      <td className="px-1.5 py-1"><CellSelect value={r.maker} onChange={(v) => setRow(r._key, { maker: v })} options={makerOpts} w="w-28" /></td>
                      <td className="px-1.5 py-1"><CellSelect value={r.supplier} onChange={(v) => setRow(r._key, { supplier: v })} options={supOpts} w="w-32" /></td>
                      <td className="px-1.5 py-1"><CellSelect value={r.material} onChange={(v) => setRow(r._key, { material: v })} options={matOpts} w="w-24" /></td>
                      <td className="px-1.5 py-1"><CellSelect value={r.production_dept} onChange={(v) => setRow(r._key, { production_dept: v })} options={deptOpts} w="w-32" /></td>
                      <td className="px-1.5 py-1"><Cell value={r.reorder_point} onChange={(v) => setRow(r._key, { reorder_point: v })} w="w-16" /></td>
                      <td className="px-1.5 py-1"><Cell value={r.cost} onChange={(v) => setRow(r._key, { cost: v })} w="w-16" /></td>
                      <td className="px-1.5 py-1">
                        <Select value={r.action} onValueChange={(v) => setRow(r._key, { action: v })}>
                          <SelectTrigger className="h-7 w-28 text-[11px]" data-testid={`ai-row-action-${r.row_no}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="create">新規登録</SelectItem>
                            {r.duplicate_of && mode === 'allow_update' && <SelectItem value="update">既存を更新</SelectItem>}
                            <SelectItem value="skip">スキップ</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-1.5 py-1 max-w-[260px]">
                        {(r.messages || []).map((msg, i) => (
                          <div key={i} className="text-[10px] text-zinc-500 leading-tight">· {msg}</div>
                        ))}
                        {r.duplicate_of && (
                          <div className="text-[10px] text-amber-600 leading-tight">既存: {r.duplicate_of.sku} {r.duplicate_of.name}</div>
                        )}
                      </td>
                      <td className="px-1.5 py-1">
                        <button type="button" onClick={() => removeRow(r._key)} data-testid={`ai-row-remove-${r.row_no}`}
                          className="w-6 h-6 rounded flex items-center justify-center text-zinc-400 hover:text-rose-600 hover:bg-rose-50">
                          <Trash size={13} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="text-[11px] text-zinc-500">
              エラー行は登録から自動的に除外されます。値を編集した場合、登録確定時にサーバー側で再検証されます。
            </div>
          </div>
        )}

        {step === 'result' && result && (
          <div className="flex-1 overflow-y-auto space-y-4" data-testid="ai-import-result">
            <div className="grid grid-cols-4 gap-3">
              {[
                ['登録', result.created, 'text-emerald-600'],
                ['更新', result.updated, 'text-sky-600'],
                ['スキップ', result.skipped, 'text-zinc-500'],
                ['エラー', result.errors?.length || 0, 'text-rose-600'],
              ].map(([label, n, cls]) => (
                <div key={label} className="rounded-lg border border-zinc-200 dark:border-zinc-700 p-3 text-center">
                  <div className={`text-2xl font-bold tabular-nums ${cls}`}>{n}</div>
                  <div className="text-[11px] text-zinc-500 mt-0.5">{label}</div>
                </div>
              ))}
            </div>
            {result.errors?.length > 0 && (
              <div className="rounded-lg border border-rose-200 bg-rose-50/50 p-3">
                <div className="flex items-center justify-between">
                  <div className="text-xs font-bold text-rose-700">一部の行のみ登録されました。エラー内容を確認してください。</div>
                  <Button variant="outline" size="sm" className="h-7 text-[11px]"
                    onClick={() => downloadErrorCsv(result.errors)} data-testid="ai-result-error-csv">
                    <DownloadSimple size={12} className="mr-1" />エラーCSV
                  </Button>
                </div>
                <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                  {result.errors.map((e, i) => (
                    <div key={i} className="text-[11px] text-rose-700">行{e.row_no} {e.name}: {e.error}</div>
                  ))}
                </div>
              </div>
            )}
            {result.batch && (
              <div className="rounded-lg border border-zinc-200 dark:border-zinc-700 p-3 flex items-center justify-between">
                <div className="text-xs text-zinc-600 dark:text-zinc-300">
                  インポート履歴に記録しました（バッチID: <span className="font-mono">{result.batch.id?.slice(0, 8)}</span>）。
                  {result.batch.undo_status === 'undone' ? ' このバッチは取消済みです。' : ' 誤りがあれば一括取消できます。'}
                </div>
                {result.batch.undo_status !== 'undone' && (
                  <Button variant="outline" size="sm" className="h-8 text-xs text-rose-600 border-rose-200 hover:bg-rose-50"
                    onClick={rollbackBatch} disabled={rollingBack} data-testid="ai-import-rollback">
                    <ArrowCounterClockwise size={13} className="mr-1" />
                    {rollingBack ? '取消中...' : `一括取消（${result.batch.item_count}件）`}
                  </Button>
                )}
              </div>
            )}
          </div>
        )}

        <DialogFooter className="border-t border-zinc-200 dark:border-zinc-800 pt-3">
          {step === 'input' && (
            <>
              <Button variant="outline" onClick={() => { reset(); onClose(); }} data-testid="ai-import-cancel">キャンセル</Button>
              <Button onClick={analyze} disabled={analyzing || (!file && !pasteText.trim())}
                className="bg-teal-700 hover:bg-teal-800 text-white" data-testid="ai-import-analyze-btn">
                {analyzing ? 'AIが解析中...' : 'AIで解析 →'}
              </Button>
            </>
          )}
          {step === 'review' && (
            <>
              <Button variant="outline" onClick={() => setStep('input')} data-testid="ai-import-back">
                <ArrowLeft size={13} className="mr-1" />戻る
              </Button>
              <Button onClick={commit} disabled={committing || importable.length === 0}
                className="bg-teal-700 hover:bg-teal-800 text-white" data-testid="ai-import-commit-btn">
                {committing ? '登録中...' : `登録を確定（${importable.length}件）`}
              </Button>
            </>
          )}
          {step === 'result' && (
            <Button onClick={() => { reset(); onClose(); }} data-testid="ai-import-close">閉じる</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
